import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const PAGE_SIZE = 10;
const FORM_STORAGE_KEY = "country_form_draft";

const api = {
  list: (search = "", is_active = "", page = 1) =>
    axiosInstance.get(
      `/api/superadmin/master/countries/?q=${encodeURIComponent(search)}&is_active=${is_active}`
    ),
  listAll: (search = "", is_active = "") =>
    axiosInstance.get(
      `/api/superadmin/master/countries/?q=${encodeURIComponent(search)}&is_active=${is_active}`
    ),
  create: (body) =>
    axiosInstance.post(`/api/superadmin/master/countries/`, body),
  patch: (id, body) =>
    axiosInstance.patch(`/api/superadmin/master/countries/${id}/`, body),
  delete: (id) =>
    axiosInstance.delete(`/api/superadmin/master/countries/${id}/`),
};

const formDraft = {
  save: (data) => {
    try { sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data)); } catch {  }
  },
  load: () => {
    try {
      const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  clear: () => {
    try { sessionStorage.removeItem(FORM_STORAGE_KEY); } catch {  }
  },
};

const Icon = {
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Search: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 11l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Edit: () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M11.5 1.5l2 2L5 12H3v-2L11.5 1.5zM10 3l2 2"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Trash: () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M2 4h11M5 4V2.5a.5.5 0 01.5-.5h4a.5.5 0 01.5.5V4M6 7v4M9 7v4M3 4l.8 8.5A1 1 0 004.8 13h5.4a1 1 0 001-.95L12 4"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Close: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Warning: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="24" fill="#FEF3C7" />
      <path d="M24 13L11 37h26L24 13z" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" fill="#FDE68A" />
      <path d="M24 22v7M24 31.5v1" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  ),
  CheckCircle: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="24" fill="#DCFCE7" />
      <circle cx="24" cy="24" r="14" stroke="#16a34a" strokeWidth="2" fill="#BBF7D0" />
      <path d="M17 24l5 5 9-9" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Success: () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="9" fill="#16a34a" />
      <path d="M5 9l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Error: () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="9" fill="#dc2626" />
      <path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  Info: () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="9" fill="#1d4ed8" />
      <path d="M9 8v5M9 5.5v.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  Loading: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 0.9s linear infinite" }}>
      <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
      <path d="M8 2a6 6 0 016 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Restore: () => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M11 6.5A4.5 4.5 0 112 4.5M2 2v2.5h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const inputBase = (hasError) => ({
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13.5, outline: "none",
  border: `1.5px solid ${hasError ? "#fca5a5" : "#e2e8f0"}`,
  background: hasError ? "#fff5f5" : "#f8fafc", color: "#0f172a",
  transition: "border 0.15s, background 0.15s", boxSizing: "border-box", fontFamily: "inherit",
});

const btnStyle = (variant = "primary") => {
  const base = {
    padding: "8px 18px", borderRadius: 8, fontSize: 13.5, fontWeight: 570,
    cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center",
    gap: 7, transition: "all 0.15s", fontFamily: "inherit", letterSpacing: "0.01em",
  };
  if (variant === "primary")    return { ...base, background: "#1d4ed8", color: "#fff", boxShadow: "0 1px 3px rgba(29,78,216,0.3)" };
  if (variant === "ghost")      return { ...base, background: "#f1f5f9", color: "#475569", border: "1.5px solid #e2e8f0" };
  if (variant === "danger")     return { ...base, background: "#dc2626", color: "#fff", boxShadow: "0 1px 3px rgba(220,38,38,0.3)" };
  if (variant === "success")    return { ...base, background: "#16a34a", color: "#fff", boxShadow: "0 1px 3px rgba(22,163,74,0.3)" };
  if (variant === "icon-edit")  return { ...base, padding: "6px 10px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #dbeafe", fontSize: 12.5 };
  if (variant === "icon-del")   return { ...base, padding: "6px 10px", background: "#fff5f5", color: "#dc2626", border: "1px solid #fee2e2", fontSize: 12.5 };
  if (variant === "icon-act")   return { ...base, padding: "6px 10px", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontSize: 12.5 };
  return base;
};

const TOAST_DURATION = 4000;

let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, createdAt: Date.now(), ...item }]);
    return id;
  },
  success: (title, msg)  => toast._push({ type: "success", title, msg }),
  error:   (title, msg)  => toast._push({ type: "error",   title, msg }),
  info:    (title, msg)  => toast._push({ type: "info",    title, msg }),
  loading: (title)       => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)          => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓",  bg: "#fff", accent: "#16a34a" },
  error:   { bar: "#dc2626", icon: "✕",  bg: "#fff", accent: "#dc2626" },
  info:    { bar: "#1d4ed8", icon: "i",  bg: "#fff", accent: "#1d4ed8" },
  loading: { bar: "#94a3b8", icon: null, bg: "#fff", accent: "#64748b" },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting,  setExiting]  = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const elapsed = Date.now() - startTs.current;
      const pct = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) { rafRef.current = requestAnimationFrame(tick); }
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{
      position: "relative", overflow: "hidden",
      background: "#fff", borderRadius: 10,
      boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
      minWidth: 300, maxWidth: 360,
      animation: exiting ? "toastOut 0.28s ease forwards" : "toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
      borderLeft: `4px solid ${meta.bar}`,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: "50%",
          background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
        }}>
          {t.type === "loading" ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "spin 0.85s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" />
            </svg>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: "2px", flexShrink: 0, display: "flex", borderRadius: 4, transition: "color 0.15s", marginTop: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <Icon.Close />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width 0.1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback((id) => { setToasts((p) => p.filter((t) => t.id !== id)); }, []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => (<ToastItem key={t.id} t={t} onDismiss={dismiss} />))}
    </div>
  );
};

const Modal = ({ isOpen, title, onClose, children, width = 520 }) => {
  const overlayRef = useRef(null);
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
        justifyContent: "center", zIndex: 1000, padding: 20, animation: "fadeIn 0.18s ease",
      }}>
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: width,
        boxShadow: "0 24px 64px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)",
        animation: "scaleIn 0.2s ease", maxHeight: "90vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 650, color: "#0f172a", letterSpacing: "-0.01em" }}>{title}</h3>
        </div>
        <div style={{ padding: "20px 24px 24px", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
};

const Field = ({ label, hint, required, error, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontSize: 13, fontWeight: 580, color: error ? "#dc2626" : "#374151", letterSpacing: "0.01em" }}>
      {label}
      {required && <span style={{ color: "#dc2626" }}>*</span>}
      {hint && <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 12 }}>{hint}</span>}
    </label>
    {children}
    {error && <p style={{ margin: "5px 0 0", fontSize: 12, color: "#dc2626" }}>⚠ {error}</p>}
  </div>
);

const StatusBadge = ({ active }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 580,
    background: active ? "#f0fdf4" : "#fef2f2",
    color: active ? "#15803d" : "#dc2626",
    border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}`,
  }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#16a34a" : "#dc2626", display: "inline-block" }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const CodePill = ({ children }) => (
  <span style={{
    background: "#f1f5f9", color: "#475569", padding: "3px 8px",
    borderRadius: 5, fontSize: 12, fontWeight: 640,
    letterSpacing: "0.04em", fontFamily: "'Courier New', monospace",
  }}>
    {children || "—"}
  </span>
);

const SkeletonRows = ({ rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i}>
        {[40, 160, 60, 60, 70, 90, 120].map((w, j) => (
          <td key={j} style={{ padding: "14px 20px" }}>
            <div style={{ height: 13, borderRadius: 5, background: "#f1f5f9", width: w, animation: "pulse 1.5s infinite" }} />
          </td>
        ))}
        <td style={{ padding: "14px 20px" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ width: 62, height: 28, borderRadius: 6, background: "#f1f5f9", animation: "pulse 1.5s infinite" }} />
            <div style={{ width: 62, height: 28, borderRadius: 6, background: "#f1f5f9", animation: "pulse 1.5s infinite" }} />
          </div>
        </td>
      </tr>
    ))}
  </>
);

// ── Pagination – identical to StateMaster: takes `total` and computes totalPages internally ──
const Pagination = ({ current, total, onChange }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  const start = (current - 1) * PAGE_SIZE + 1;
  const end   = Math.min(current * PAGE_SIZE, total);

  const getPages = () => {
    const pages = [];
    const delta = 2;
    let last = 0;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
        if (last && i - last > 1) pages.push("...");
        pages.push(i);
        last = i;
      }
    }
    return pages;
  };

  const PBtn = ({ n }) => (
    <button onClick={() => onChange(n)}
      style={{ ...btnStyle(n === current ? "primary" : "ghost"), padding: "5px 11px", minWidth: 34, justifyContent: "center" }}>
      {n}
    </button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>
        Showing <strong style={{ color: "#374151" }}>{start}–{end}</strong> of <strong style={{ color: "#374151" }}>{total}</strong> results
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button disabled={current === 1} onClick={() => onChange(current - 1)}
          style={{ ...btnStyle("ghost"), padding: "5px 9px", opacity: current === 1 ? 0.4 : 1, cursor: current === 1 ? "not-allowed" : "pointer" }}>
          <Icon.ChevronLeft />
        </button>
        {getPages().map((p, i) =>
          p === "..." ? (
            <span key={`d${i}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: 13, lineHeight: "34px" }}>…</span>
          ) : (
            <PBtn key={p} n={p} />
          )
        )}
        <button disabled={current === totalPages} onClick={() => onChange(current + 1)}
          style={{ ...btnStyle("ghost"), padding: "5px 9px", opacity: current === totalPages ? 0.4 : 1, cursor: current === totalPages ? "not-allowed" : "pointer" }}>
          <Icon.ChevronRight />
        </button>
      </div>
    </div>
  );
};

const StatsBar = ({ stats, loading }) => {
  const Card = ({ label, count, color, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 10, background: bg, border: `1px solid ${border}`, minWidth: 110 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
          {loading
            ? <div style={{ width: 28, height: 16, borderRadius: 4, background: "#e2e8f0", animation: "pulse 1.5s infinite" }} />
            : count}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2, letterSpacing: "0.02em" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <Card label="Total Countries" count={stats.total}    color="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <Card label="Active"          count={stats.active}   color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <Card label="Inactive"        count={stats.inactive} color="#dc2626" bg="#fef2f2" border="#fecaca" />
    </div>
  );
};

const ConfirmDialog = ({ isOpen, country, onConfirm, onCancel, loading }) => (
  <Modal isOpen={isOpen} title="Confirm Deactivation" onClose={onCancel} width={420}>
    <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
        <Icon.Warning />
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "#1e293b" }}>
        Deactivate &quot;{country?.name}&quot;?
      </p>
      <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
        This will mark the country as inactive. You can reactivate it later.
      </p>
      {country && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
          {country.iso2     && <CodePill>{country.iso2}</CodePill>}
          {country.iso3     && <CodePill>{country.iso3}</CodePill>}
          {country.currency && <CodePill>{country.currency}</CodePill>}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button onClick={onCancel} disabled={loading} style={{ ...btnStyle("ghost"), opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading}
          style={{ ...btnStyle("danger"), opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "Deactivating…" : "Yes, Deactivate"}
        </button>
      </div>
    </div>
  </Modal>
);

const ConfirmActivateDialog = ({ isOpen, country, onConfirm, onCancel, loading }) => (
  <Modal isOpen={isOpen} title="Confirm Activation" onClose={onCancel} width={420}>
    <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
        <Icon.CheckCircle />
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "#1e293b" }}>
        Activate &quot;{country?.name}&quot;?
      </p>
      <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
        This will mark the country as active and make it available for use.
      </p>
      {country && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
          {country.iso2     && <CodePill>{country.iso2}</CodePill>}
          {country.iso3     && <CodePill>{country.iso3}</CodePill>}
          {country.currency && <CodePill>{country.currency}</CodePill>}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button onClick={onCancel} disabled={loading} style={{ ...btnStyle("ghost"), opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading}
          style={{ ...btnStyle("success"), opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "Activating…" : "Yes, Activate"}
        </button>
      </div>
    </div>
  </Modal>
);

const DraftBanner = ({ onRestore, onDiscard }) => (
  <div style={{
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
    padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 10, flexWrap: "wrap",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 16 }}>📝</span>
      <div>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#92400e" }}>Unsaved draft found</p>
        <p style={{ margin: 0, fontSize: 12, color: "#b45309" }}>You have a previously unsaved form. Would you like to restore it?</p>
      </div>
    </div>
    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
      <button onClick={onDiscard} style={{ ...btnStyle("ghost"), padding: "5px 12px", fontSize: 12.5 }}>Discard</button>
      <button onClick={onRestore} style={{ ...btnStyle("primary"), padding: "5px 12px", fontSize: 12.5, background: "#d97706", boxShadow: "none" }}
        onMouseEnter={e => e.currentTarget.style.background = "#b45309"}
        onMouseLeave={e => e.currentTarget.style.background = "#d97706"}>
        <Icon.Restore /> Restore
      </button>
    </div>
  </div>
);

const EMPTY_FORM = { name: "", iso2: "", iso3: "", currency: "", is_active: true };

const CountryForm = ({ mode, initial, onClose, onSave }) => {
  const isEdit = mode === "edit";

  const savedDraft   = !isEdit ? formDraft.load() : null;
  const [showBanner, setShowBanner] = useState(!isEdit && !!savedDraft);

  const [form,    setForm]    = useState(initial || EMPTY_FORM);
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  useEffect(() => {
    if (isEdit || showBanner) return;
    formDraft.save(form);
  }, [form, isEdit, showBanner]);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: val }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: null }));
  };

  const handleRestore = () => { if (savedDraft) setForm(savedDraft); setShowBanner(false); };
  const handleDiscard = () => { formDraft.clear(); setShowBanner(false); setForm(EMPTY_FORM); };

  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = "Country name is required.";
    if (!form.iso2?.trim()) errs.iso2 = "ISO2 code is required.";
    else if (!/^[A-Za-z]{2}$/.test(form.iso2)) errs.iso2 = "Must be exactly 2 letters.";
    if (form.iso3     && !/^[A-Za-z]{3}$/.test(form.iso3))     errs.iso3 = "Must be exactly 3 letters.";
    if (form.currency && !/^[A-Za-z]{3}$/.test(form.currency)) errs.currency = "Must be exactly 3 letters (e.g. INR).";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await onSave({
        name:      form.name.trim(),
        iso2:      form.iso2.toUpperCase(),
        iso3:      form.iso3?.toUpperCase() || "",
        currency:  form.currency?.toUpperCase() || "",
        is_active: form.is_active,
      });
      formDraft.clear();
    } catch (apiErrs) {
      if (apiErrs && typeof apiErrs === "object") {
        const mapped = {};
        Object.keys(apiErrs).forEach((k) => {
          mapped[k] = Array.isArray(apiErrs[k]) ? apiErrs[k][0] : String(apiErrs[k]);
        });
        setErrors(mapped);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => { formDraft.clear(); onClose(); };

  const focusStyle = (e) => { e.target.style.borderColor = "#1d4ed8"; e.target.style.background = "#fff"; };
  const blurStyle  = (e, hasErr) => {
    e.target.style.borderColor = hasErr ? "#fca5a5" : "#e2e8f0";
    e.target.style.background  = hasErr ? "#fff5f5" : "#f8fafc";
  };

  return (
    <div>
      {showBanner && <DraftBanner onRestore={handleRestore} onDiscard={handleDiscard} />}

      {errors.__global && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 550 }}>⚠ {errors.__global}</p>
        </div>
      )}

      <Field label="Country Name" required error={errors.name}>
        <input ref={firstRef} type="text" value={form.name} onChange={set("name")}
          placeholder="e.g. India" style={inputBase(!!errors.name)}
          onFocus={focusStyle} onBlur={(e) => blurStyle(e, !!errors.name)} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 18 }}>
        <Field label="ISO2" hint="2-letter" required error={errors.iso2}>
          <input type="text" value={form.iso2} onChange={set("iso2")} placeholder="IN" maxLength={2}
            style={{ ...inputBase(!!errors.iso2), textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "monospace" }}
            onFocus={focusStyle} onBlur={(e) => blurStyle(e, !!errors.iso2)} />
        </Field>
        <Field label="ISO3" hint="3-letter " error={errors.iso3}>
          <input type="text" value={form.iso3} onChange={set("iso3")} placeholder="IND" maxLength={3}
            style={{ ...inputBase(!!errors.iso3), textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "monospace" }}
            onFocus={focusStyle} onBlur={(e) => blurStyle(e, !!errors.iso3)} />
        </Field>
        <Field label="Currency" hint="3-letter " error={errors.currency}>
          <input type="text" value={form.currency} onChange={set("currency")} placeholder="INR" maxLength={3}
            style={{ ...inputBase(!!errors.currency), textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "monospace" }}
            onFocus={focusStyle} onBlur={(e) => blurStyle(e, !!errors.currency)} />
        </Field>
      </div>

      <Field label="Status">
        <div onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", userSelect: "none", transition: "border 0.15s", width: "fit-content" }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1d4ed8"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}>
          <div style={{ width: 38, height: 22, borderRadius: 11, background: form.is_active ? "#1d4ed8" : "#cbd5e1", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: 3, left: form.is_active ? 19 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </div>
          <span style={{ fontSize: 13, color: form.is_active ? "#15803d" : "#64748b", fontWeight: 550 }}>
            {form.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </Field>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
        <button onClick={handleCancel} disabled={loading} style={{ ...btnStyle("ghost"), opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={handleSubmit} disabled={loading}
          style={{ ...btnStyle("primary"), opacity: loading ? 0.8 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading
            ? (isEdit ? "Saving…" : "Creating…")
            : (isEdit ? "Save Changes" : "Create Country")}
        </button>
      </div>
    </div>
  );
};

export default function CountryMaster() {
  // ── All countries stored at once – client-side pagination like StateMaster ──
  const [countries,       setCountries]       = useState([]);
  const [page,            setPage]            = useState(1);           // simple page state, identical to StateMaster
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState("");
  const [activeFilter,    setActiveFilter]    = useState("");
  const [modal,           setModal]           = useState(null);
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [deletingId,      setDeletingId]      = useState(null);
  const [activatingId,    setActivatingId]    = useState(null);
  const [activateTarget,  setActivateTarget]  = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);
  const [stats,           setStats]           = useState({ total: 0, active: 0, inactive: 0 });
  const [statsLoading,    setStatsLoading]    = useState(true);

  const debounceRef = useRef(null);

  // ── Stats: 3 parallel requests reading json.count (server total, not page slice) ──
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [resAll, resActive, resInactive] = await Promise.all([
        api.list("", "",      1),
        api.list("", "true",  1),
        api.list("", "false", 1),
      ]);
      const [dataAll, dataActive, dataInactive] = await Promise.all([
        resAll.ok      ? Promise.resolve(resAll.data)      : Promise.resolve(null),
        resActive.ok   ? Promise.resolve(resActive.data)   : Promise.resolve(null),
        resInactive.ok ? Promise.resolve(resInactive.data) : Promise.resolve(null),
      ]);
      const total    = dataAll      ? (dataAll.count      ?? (Array.isArray(dataAll)      ? dataAll.length      : (dataAll.results      ?? []).length)) : 0;
      const active   = dataActive   ? (dataActive.count   ?? (Array.isArray(dataActive)   ? dataActive.length   : (dataActive.results   ?? []).length)) : 0;
      const inactive = dataInactive ? (dataInactive.count ?? (Array.isArray(dataInactive) ? dataInactive.length : (dataInactive.results ?? []).length)) : 0;
      setStats({ total, active, inactive });
    } catch { /* silently ignore */ }
    finally { setStatsLoading(false); }
  }, []);

  // ── Fetch ALL countries (no page param) – sort active first, reset to page 1 ──
  // Exactly mirrors StateMaster's fetchStates pattern
  const fetchCountries = useCallback(async (q, active) => {
    setLoading(true);
    try {
      const { data } = await api.listAll(q, active);

      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); setLoading(false); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view countries."); setLoading(false); return; }
      if (data?.status >= 400) throw new Error("Load failed");

      const results = Array.isArray(data) ? data : (data?.results ?? []);

      setCountries([...results].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0)));
      setPage(1);   // reset to page 1 on every fresh fetch, identical to StateMaster
    } catch (err) {
      if (err?.name === "TypeError") {
        toast.error("Network Error", "Unable to reach the server. Check your connection.");
      } else {
        toast.error("Failed to Load", "Unable to fetch countries. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCountries(search, activeFilter);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced re-fetch on search / filter change
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCountries(search, activeFilter);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, activeFilter, fetchCountries]);

  // ── Client-side slice – identical to StateMaster's `paged` ──
  const paged = countries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCreate = async (payload) => {
    const tid = toast.loading("Creating country…");
    try {
      const { data } = await api.create(payload);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && typeof data?.data === "object" && !data?.data?.detail) { throw data?.data; }
        const msg = data?.data?.detail || `Unexpected error (${data?.status}). Please try again.`;
        toast.error("Create Failed", msg);
        return;
      }

      const created = data?.data ?? data;
      toast.success("Country Added", `"${created?.name || payload.name}" was added successfully.`);
      setModal(null);
      fetchCountries(search, activeFilter);
      fetchStats();
    } catch (apiErrs) {
      toast.dismiss(tid);
      if (apiErrs && typeof apiErrs === "object" && !apiErrs.message) throw apiErrs;
      toast.error("Network Error", "Could not reach the server. Please try again.");
    }
  };

  const handlePatch = async (id, payload) => {
    const tid = toast.loading("Saving changes…");
    try {
      const { data } = await api.patch(id, payload);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && typeof data?.data === "object" && !data?.data?.detail) { throw data?.data; }
        const msg = data?.data?.detail || `Update failed (${data?.status}). Please try again.`;
        toast.error("Save Failed", msg);
        return;
      }

      const updated = data?.data?.data ?? data?.data ?? data;
      toast.success("Changes Saved", `"${updated?.name || payload.name}" updated successfully.`);
      setModal(null);
      fetchCountries(search, activeFilter);
      fetchStats();
    } catch (apiErrs) {
      toast.dismiss(tid);
      if (apiErrs && typeof apiErrs === "object" && !apiErrs.message) throw apiErrs;
      toast.error("Network Error", "Could not reach the server. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    const tid = toast.loading(`Deactivating "${deleteTarget.name}"…`);
    try {
      const { data } = await api.delete(deleteTarget.id);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Deactivation failed (${data?.status}). Please try again.`;
        toast.error("Deactivate Failed", msg);
        return;
      }

      toast.success("Deactivated!", data?.data?.message || `"${deleteTarget.name}" has been deactivated.`);
      setDeleteTarget(null);
      fetchCountries(search, activeFilter);
      fetchStats();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not complete deactivation. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleActivateConfirm = async () => {
    if (!activateTarget) return;
    setActivateLoading(true);
    setActivatingId(activateTarget.id);
    const tid = toast.loading(`Activating "${activateTarget.name}"…`);
    try {
      const { data } = await api.patch(activateTarget.id, { is_active: true });
      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Activation failed (${data?.status}). Please try again.`;
        toast.error("Activate Failed", msg);
        return;
      }

      toast.success("Activated!", `"${activateTarget.name}" is now active.`);
      setActivateTarget(null);
      fetchCountries(search, activeFilter);
      fetchStats();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not activate. Please try again.");
    } finally {
      setActivateLoading(false);
      setActivatingId(null);
    }
  };

  const hasFilters = search || activeFilter;

  const TH = ({ children, center }) => (
    <th style={{ padding: "11px 20px", textAlign: center ? "center" : "left", fontSize: 11.5, fontWeight: 650, color: "#64748b", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {children}
    </th>
  );

  const TD = ({ children, center, muted }) => (
    <td style={{ padding: "13px 20px", fontSize: 13.5, color: muted ? "#94a3b8" : "#1e293b", borderBottom: "1px solid #f8fafc", textAlign: center ? "center" : "left" }}>
      {children}
    </td>
  );

  return (
    <>
      <style>{`
        @keyframes fadeIn   { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn  { from { opacity: 0; transform: scale(0.96) translateY(-8px) } to { opacity: 1; transform: none } }
        @keyframes pulse    { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }
        @keyframes toastIn  { from { opacity: 0; transform: translateX(24px) scale(0.97) } to { opacity: 1; transform: translateX(0) scale(1) } }
        @keyframes toastOut { from { opacity: 1; transform: translateX(0) scale(1) } to { opacity: 0; transform: translateX(28px) scale(0.95) } }
        @keyframes spin     { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar       { width: 6px; height: 6px; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
      `}</style>

      <ToastContainer />

      <div style={{ fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: "100vh", background: "#f8fafc" }}>
        <div style={{ padding: "24px" }}>

          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#0f172a" }}>Country Master</h1>
              <p style={{ margin: 0, fontSize: 12.5, color: "#94a3b8" }}>Manage country master data</p>
            </div>
            <button onClick={() => setModal({ mode: "create" })} style={btnStyle("primary")}
              onMouseEnter={(e) => e.currentTarget.style.background = "#1e40af"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#1d4ed8"}>
              <Icon.Plus /> Add Country
            </button>
          </div>

          <StatsBar stats={stats} loading={statsLoading} />

          <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #e2e8f0", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                <Icon.Search />
              </span>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries…"
                style={{ ...inputBase(false), paddingLeft: 34 }}
                onFocus={(e) => e.target.style.borderColor = "#1d4ed8"}
                onBlur={(e)  => e.target.style.borderColor = "#e2e8f0"} />
            </div>
            <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
              style={{ ...inputBase(false), flex: "0 1 140px", cursor: "pointer", appearance: "none" }}>
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            {hasFilters && (
              <button onClick={() => { setSearch(""); setActiveFilter(""); }}
                style={{ ...btnStyle("ghost"), background: "#fff5f5", color: "#dc2626", borderColor: "#fecaca", fontSize: 13 }}>
                Clear
              </button>
            )}
          </div>

          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead>
                  <tr>
                    <TH>Sr No.</TH>
                    <TH>Country</TH>
                    <TH>ISO2</TH>
                    <TH>ISO3</TH>
                    <TH>Currency</TH>
                    <TH center>Status</TH>
                    <TH>Created At</TH>
                    <TH center>Actions</TH>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows rows={8} />
                  ) : paged.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: "56px 20px", textAlign: "center" }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600, color: "#374151", marginBottom: 5 }}>No countries found</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>
                          {hasFilters ? "Try adjusting your filters or search term" : "Click \"Add Country\" to get started"}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paged.map((c, idx) => {
                      // ── Sr No. uses client-side page + idx, identical to StateMaster ──
                      const globalIdx = (page - 1) * PAGE_SIZE + idx + 1;
                      const isBusy = deletingId === c.id || activatingId === c.id;
                      return (
                        <tr key={c.id}
                          style={{ transition: "background 0.1s", opacity: isBusy ? 0.35 : 1, pointerEvents: isBusy ? "none" : "auto" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#fafbff"}
                          onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                          <TD muted>{globalIdx}</TD>
                          <TD><span style={{ fontWeight: 570 }}>{c.name}</span></TD>
                          <TD><CodePill>{c.iso2 || "—"}</CodePill></TD>
                          <TD><CodePill>{c.iso3 || "—"}</CodePill></TD>
                          <TD><CodePill>{c.currency || "—"}</CodePill></TD>
                          <TD center><StatusBadge active={c.is_active} /></TD>
                          <TD muted><span style={{ fontSize: 12.5 }}>{c.created_at || "—"}</span></TD>
                          <td style={{ padding: "10px 20px", borderBottom: "1px solid #f8fafc" }}>
                            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                              <button title="Edit" onClick={() => setModal({ mode: "edit", data: c })}
                                style={btnStyle("icon-edit")}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#dbeafe"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "#eff6ff"}>
                                <Icon.Edit /> Edit
                              </button>
                              {c.is_active ? (
                                <button title="Deactivate" onClick={() => setDeleteTarget(c)}
                                  style={btnStyle("icon-del")}
                                  onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff5f5"}>
                                  Deactivate
                                </button>
                              ) : (
                                <button title="Activate" onClick={() => setActivateTarget(c)}
                                  style={btnStyle("icon-act")}
                                  onMouseEnter={(e) => e.currentTarget.style.background = "#dcfce7"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "#f0fdf4"}>
                                  Activate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination: same call signature as StateMaster ── */}
            {!loading && countries.length > PAGE_SIZE && (
              <Pagination
                current={page}
                total={countries.length}
                onChange={setPage}
              />
            )}
          </div>

        </div>
      </div>

      <Modal
        isOpen={modal?.mode === "create"}
        title="Add New Country"
        onClose={() => { formDraft.clear(); setModal(null); }}>
        <CountryForm mode="create" onClose={() => { formDraft.clear(); setModal(null); }} onSave={handleCreate} />
      </Modal>

      <Modal
        isOpen={modal?.mode === "edit"}
        title={modal?.data ? `Edit — ${modal.data.name}` : "Edit Country"}
        onClose={() => setModal(null)}>
        <CountryForm
          mode="edit"
          initial={modal?.data
            ? {
                name:      modal.data.name      ?? "",
                iso2:      modal.data.iso2       ?? "",
                iso3:      modal.data.iso3       ?? "",
                currency:  modal.data.currency   ?? "",
                is_active: modal.data.is_active  ?? true,
              }
            : EMPTY_FORM}
          onClose={() => setModal(null)}
          onSave={(payload) => handlePatch(modal.data.id, payload)}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        country={deleteTarget}
        loading={!!deletingId}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmActivateDialog
        isOpen={!!activateTarget}
        country={activateTarget}
        loading={activateLoading}
        onConfirm={handleActivateConfirm}
        onCancel={() => setActivateTarget(null)}
      />
    </>
  );
}